import { memo, useEffect, useMemo, useState } from 'react';
import {
  Box, Typography, CircularProgress, Button, Breadcrumbs, Link, Stack,
} from '@mui/material';
import { Refresh as RefreshIcon, PlayArrow as PlayArrowIcon } from '@mui/icons-material';
import SnackbarAlert from '../../../shared/components/SnackbarAlert';
import { useSnackbar } from '../../../shared/hooks/useSnackbar';
import { useCatalogService } from '../../service-administration/services/catalogService';
import LandscapeMatrixView from './LandscapeMatrix';
import SpokeView from './SpokeView';
import SpokeActivityView from './SpokeActivityView';
import { useLandscapeMatrix, cellKey } from '../hooks/useLandscapeMatrix';

interface LandscapeViewProps {
  customerId: string | undefined;
  customerName?: string;
  /** Rendered while active/visible (host controls mount for lazy fetch). */
  active?: boolean;
}

type InternalView =
  | { kind: 'matrix' }
  | { kind: 'spoke'; accountId: string; region: string }
  | { kind: 'activity'; accountId: string; region: string };

const KPIS = [
  { key: 'inSync', label: 'in sync', color: 'success.main' },
  { key: 'attention', label: 'need action', color: 'warning.main' },
  { key: 'blocked', label: 'blocked', color: 'error.main' },
  { key: 'reconciling', label: 'reconciling', color: 'info.main' },
  { key: 'pendingSync', label: 'pending sync', color: 'text.secondary' },
] as const;

/**
 * Account×region deployment-health matrix with spoke/activity drill-down.
 * Extracted from the former LandscapeModal so it can be hosted as the "Landscape"
 * view-mode inside the Deployments surface (and anywhere else). No Dialog chrome.
 */
const LandscapeView = memo<LandscapeViewProps>(({ customerId, customerName, active = true }) => {
  const matrix = useLandscapeMatrix(customerId, active);
  const catalogService = useCatalogService();
  const { snackbar, showSnackbar, closeSnackbar } = useSnackbar();
  const [view, setView] = useState<InternalView>({ kind: 'matrix' });
  const [reconciling, setReconciling] = useState(false);

  useEffect(() => {
    if (!active) setView({ kind: 'matrix' });
  }, [active]);

  const runReconciler = async () => {
    setReconciling(true);
    try {
      await catalogService.triggerReconciler('deployment');
      showSnackbar('Deployment reconciler triggered — state will refresh shortly.', 'success');
      setTimeout(() => matrix.refresh(), 4000);
    } catch (err) {
      showSnackbar(err instanceof Error ? err.message : 'Failed to trigger reconciler', 'error');
    } finally {
      setReconciling(false);
    }
  };

  const accountNameOf = useMemo(
    () => (accountId: string) => matrix.accounts.find((a) => a.awsAccountId === accountId)?.name ?? accountId,
    [matrix.accounts],
  );

  const body = () => {
    if (!customerId) {
      return <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography color="text.secondary">Select a customer to view the landscape.</Typography>
      </Box>;
    }
    if (matrix.loading) {
      return <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><CircularProgress /></Box>;
    }
    if (matrix.error) {
      return <Box sx={{ p: 3 }}><Typography color="error">{matrix.error}</Typography></Box>;
    }
    if (view.kind === 'spoke') {
      return <SpokeView
        customerId={customerId}
        accountId={view.accountId}
        region={view.region}
        matrix={matrix}
        onOpenActivity={() => setView({ kind: 'activity', accountId: view.accountId, region: view.region })}
      />;
    }
    if (view.kind === 'activity') {
      const deployments = matrix.deploymentsByCell.get(cellKey(view.accountId, view.region)) ?? [];
      return <SpokeActivityView customerId={customerId} deployments={deployments} active />;
    }
    return <LandscapeMatrixView
      matrix={matrix}
      onSelectCell={(accountId, region) => setView({ kind: 'spoke', accountId, region })}
    />;
  };

  const breadcrumbs = view.kind === 'matrix' ? null : (
    <Breadcrumbs sx={{ px: 3, py: 1, borderBottom: 1, borderColor: 'divider' }}>
      <Link component="button" underline="hover" color="inherit" onClick={() => setView({ kind: 'matrix' })}>
        {customerName ?? 'Landscape'}
      </Link>
      {view.kind === 'activity' ? (
        <Link component="button" underline="hover" color="inherit"
          onClick={() => setView({ kind: 'spoke', accountId: view.accountId, region: view.region })}>
          {accountNameOf(view.accountId)} · {view.region}
        </Link>
      ) : (
        <Typography color="text.primary">{accountNameOf(view.accountId)} · {view.region}</Typography>
      )}
      {view.kind === 'activity' && <Typography color="text.primary">Activity</Typography>}
    </Breadcrumbs>
  );

  return (
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}>
      {customerId && !matrix.loading && !matrix.error && view.kind === 'matrix' && (
        <Box sx={{ px: 3, py: 1.25, borderBottom: 1, borderColor: 'divider' }}>
          <Stack direction="row" gap={2} flexWrap="wrap" alignItems="center">
            {KPIS.map((k) => (
              <Box key={k.key}>
                <Typography variant="h6" sx={{ color: k.color, lineHeight: 1 }}>{matrix.summary[k.key]}</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.4 }}>{k.label}</Typography>
              </Box>
            ))}
            <Stack direction="row" gap={1} sx={{ ml: 'auto' }}>
              <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>
                {matrix.accounts.length} accounts · {matrix.regions.length} regions · {matrix.summary.total} deployments
              </Typography>
              <Button size="small" startIcon={<PlayArrowIcon fontSize="small" />} onClick={runReconciler}
                disabled={reconciling || !customerId} sx={{ color: 'text.secondary' }}>
                {reconciling ? 'Reconciling…' : 'Run reconciler'}
              </Button>
              <Button size="small" startIcon={<RefreshIcon fontSize="small" />} onClick={() => matrix.refresh()} sx={{ color: 'text.secondary' }}>
                Refresh
              </Button>
            </Stack>
          </Stack>
        </Box>
      )}
      {breadcrumbs}
      {body()}
      <SnackbarAlert snackbar={snackbar} onClose={closeSnackbar} />
    </Box>
  );
});

LandscapeView.displayName = 'LandscapeView';
export default LandscapeView;
